import { BaseListViewStore, BooleanToggleCellData } from 'core/stores';
import { observable, action, makeObservable } from 'mobx';
import { ModalParams, ConfirmModalParams } from 'core/models';
import { checkPermissions } from 'core/stores';
import {
	checkForColumnWidth,
	isSuperAdminOrganization,
	isWarehouse,
	notificationError,
	TableFilterUtility,
} from 'core/utils';
import { localStorageHandler } from 'core/services';
import { LookupStore } from 'modules/lookup/stores';
import { ReturnAddressStore } from 'modules/return-address/stores';
import { sourceOfChange } from 'common/constants';
import { TableFilterTemplate } from 'themes/components';
import { ReturnAddressService } from 'common/data';
import _ from 'lodash';

class ReturnAddressListViewStore extends BaseListViewStore {
	@observable selectedItem = null;
	isSuperAdmin = false;
	showActions = true;
	localStorageWidthKey = 'users-column-width';
	seller = null;
	selectedItem = null;
	isEdit = false;
	seller = null;

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ReturnAddressStore(rootStore),
			queryParams: {
				embed: ['seller', 'address', 'address.coreCountry', 'address.coreCountryState'],
				filter: localStorageHandler.get('return-address-filter'),
			},
			findFunction: 'findReturnAddress',
			tableData: {
				columns: [
					{
						key: 'name',
						title: 'RETURNADDRESSVIEW.NAME',
						sortable: true,
						selected: true,
						filter: null,
					},
					{
						key: 'fullAddress',
						title: 'RETURNADDRESSVIEW.RETURNADDRESS',
						sortable: false,
						selected: true,
					},
					{
						key: 'isDefaultReturnAddress',
						title: 'RETURNADDRESSVIEW.ISDEFAULTRETURNADDRESS',
						dataCell: BooleanToggleCellData,
						selected: true,
						sortable: false,
						filter: null,
						onClick: returnAddress => this.setToDefaultReturnAddress(returnAddress),
					},
					{
						key: 'seller.applicationTitle',
						title: 'RETURNADDRESSVIEW.SELLER',
						sortable: false,
						selected: true,
						enabled: isSuperAdminOrganization(rootStore),
						filter: null,
					},
				],
				actions: {
					onReturnAddressEdit: returnAddress => this.openEditModal(returnAddress),
					onReturnAddressDelete: async returnAddress => this.openDeleteConfirmModal(returnAddress),
				},
				sortObject: [],
			},
			routes: {
				create: () => {
					this.editModal.open();
				},
			},
			permissions: {
				update: () => this.authStore.hasPermission(''),
				create: () => this.authStore.hasPermission(''),
				remove: () => this.authStore.hasPermission(''),
				read: () => this.authStore.hasPermission(''),
				full: () => this.authStore.hasPermission(''),
			},
		});
		makeObservable(this);

		this.seller = localStorageHandler.get('selectedSeller');
		if (isSuperAdminOrganization(rootStore)) {
			this.isSuperAdmin = true;
		}
		if (isWarehouse(rootStore) || isSuperAdminOrganization(rootStore)) {
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		this.queryUtility.initialize(this.findReturnAddress);

		this.showActions = checkPermissions(this).actionsAllowed;
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.columnFilterSetup = 'return-address-data';
		this.ReturnAddressStore = new ReturnAddressStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.tableFilter = new TableFilterUtility({
			lookupStore: this.lookupStore,
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			services: [new ReturnAddressService(this.rootStore.app.baasic.apiClient)],
		});
		this.setHeaderModes();
	}
	@action.bound
	openEditModal(item) {
		this.selectedItem = item;
		this.isEdit = true;
		this.editModal.open();
	}

	@action.bound
	openDeleteConfirmModal = returnAddress => {
		this.selectedItem = returnAddress;
		this.deleteConfirmModal.open({
			message: ['RETURNADDRESSVIEW.CONFIRMDELETE', { name: returnAddress.name }],
		});
	};

	@action.bound
	setToDefaultReturnAddress = async returnAddress => {
		let setDefault;
		if (returnAddress.isDefaultReturnAddress) {
			returnAddress.isDefaultReturnAddress = false;
			setDefault = 'RETURNADDRESSVIEW.UNSET';
		} else {
			returnAddress.isDefaultReturnAddress = true;
			setDefault = 'RETURNADDRESSVIEW.SET';
		}
		try {
			await this.listStore.updateReturnAddress(returnAddress, sourceOfChange.updateReturnAddress);
			this.rootStore.notificationStore.success([
				'RETURNADDRESSVIEW.SETDEFAULTSUCCESMESSAGE',
				{ blank: setDefault, name: returnAddress.name },
			]);
			await this.findReturnAddress();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	deleteReturnAddress = async returnAddress => {
		this.suspend();
		try {
			await this.listStore.deleteReturnAddress(returnAddress.id);
			this.rootStore.notificationStore.success(['RETURNADDRESSVIEW.DELETESUCCESS', { name: returnAddress.name }]);
			await this.findReturnAddress();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	findReturnAddress = async () => {
		this.suspend();
		this.isEdit = false;
		this.setButtons();
		if (this.isSuperAdmin) {
			this.seller = this.seller;
		} else {
			this.seller = this.rootStore.applicationStore.currentOrganization;
		}
		this.queryUtility.setFilterParams(this, null, this.tableFilter);
		await this.listStore.findReturnAddress(this.queryUtility.filter);

		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.resume();
	};

	@action.bound
	setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-plus spc--right--sml',
					onClick: this.routes.create,
					authorization: 'channelSection.create',
					text: 'RETURNADDRESSNEW',
				},
			],
		});
	}

	editModal = new ModalParams({
		onClose: () => {
			this.isEdit = false;
			this.findReturnAddress();
		},
	});

	createModal = new ModalParams({
		onClose: this.closeModal,
	});

	deleteModal = new ModalParams({
		onClose: this.closeModal,
	});

	deleteConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			if (this.selectedItem != null) {
				this.deleteReturnAddress(this.selectedItem);
			}
		},
	});
}

export default ReturnAddressListViewStore;



// WEBPACK FOOTER //
// ./src/modules/return-address/stores/ReturnAddressListViewStore.js